import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Copy, CheckCircle, Download, Webhook, Mail } from 'lucide-react';

type TabType = 'articles' | 'mailinglist';

const BASE_URL = 'https://app.dibbi.nl/functions/v1';
const ARTICLES_URL = `${BASE_URL}/articles-webhook`;
const MAILINGLIST_URL = `${BASE_URL}/add-email-contact`;

interface CodeBlockProps {
  code: string;
  language?: string;
  colorClass?: string;
  sectionKey: string;
  copiedSection: string | null;
  onCopy: (text: string, key: string) => void;
}

function CodeBlock({ code, colorClass = 'text-green-400', sectionKey, copiedSection, onCopy }: CodeBlockProps) {
  return (
    <div className="relative">
      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
        <pre className={`text-sm font-mono ${colorClass}`}>{code}</pre>
      </div>
      <button
        onClick={() => onCopy(code, sectionKey)}
        className="absolute top-3 right-3 p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 hover:text-white transition-colors"
        title="Kopieer"
      >
        {copiedSection === sectionKey ? (
          <CheckCircle className="w-4 h-4 text-green-400" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
      </button>
    </div>
  );
}

function EndpointBadge({ method }: { method: 'GET' | 'POST' }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold font-mono ${
      method === 'GET' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
    }`}>
      {method}
    </span>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-5">
      <h3 className="font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}

export function AdminApiDocumentation() {
  const navigate = useNavigate();
  const { authUser, isAdmin, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('articles');
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading) {
      if (!authUser) {
        navigate('/admin', { replace: true });
        return;
      }
      if (!isAdmin) {
        navigate('/dashboard', { replace: true });
        return;
      }
    }
  }, [authUser, isAdmin, authLoading, navigate]);

  const handleCopy = (text: string, section: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const articlesCurl = `curl -X GET "${ARTICLES_URL}" \\
  -H "X-API-Key: YOUR_API_KEY"`;

  const articlesJs = `const response = await fetch('${ARTICLES_URL}', {
  headers: { 'X-API-Key': 'YOUR_API_KEY' }
});
const { articles, total } = await response.json();`;

  const articlesPython = `import requests

r = requests.get('${ARTICLES_URL}', headers={'X-API-Key': 'YOUR_API_KEY'})
data = r.json()
print(data['articles'])`;

  const mailinglistCurl = `curl -X POST "${MAILINGLIST_URL}" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "jan@bedrijf.nl", "name": "Jan de Vries"}'`;

  const mailinglistJs = `const response = await fetch('${MAILINGLIST_URL}', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    email: 'jan@bedrijf.nl',
    name: 'Jan de Vries'
  })
});
const result = await response.json();
// { success: true, action: 'created', email: 'jan@bedrijf.nl' }`;

  const mailinglistPython = `import requests

r = requests.post(
    '${MAILINGLIST_URL}',
    headers={
        'X-API-Key': 'YOUR_API_KEY',
        'Content-Type': 'application/json'
    },
    json={'email': 'jan@bedrijf.nl', 'name': 'Jan de Vries'}
)
print(r.json())`;

  const mailinglistZapier = `// Zapier "Code by Zapier" stap (JavaScript)
const response = await fetch('${MAILINGLIST_URL}', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    email: inputData.email,
    name: inputData.name || ''
  })
});
const result = await response.json();
return result;`;

  const postmanCollection = {
    info: {
      name: 'Dibbi API',
      description: 'Dibbi API endpoints - Articles Webhook en Mailinglijst Webhook',
      schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json'
    },
    variable: [
      { key: 'API_KEY', value: 'YOUR_API_KEY', type: 'string' },
      { key: 'BASE_URL', value: BASE_URL, type: 'string' },
    ],
    item: [
      {
        name: 'Articles Webhook',
        item: [
          {
            name: 'Get Latest Articles',
            request: {
              method: 'GET',
              header: [{ key: 'X-API-Key', value: '{{API_KEY}}', type: 'text' }],
              url: { raw: `${ARTICLES_URL}`, protocol: 'https', host: ['app', 'dibbi', 'nl'], path: ['functions', 'v1', 'articles-webhook'] }
            },
            response: []
          }
        ]
      },
      {
        name: 'Mailinglijst Webhook',
        item: [
          {
            name: 'Add Email Contact',
            request: {
              method: 'POST',
              header: [
                { key: 'X-API-Key', value: '{{API_KEY}}', type: 'text' },
                { key: 'Content-Type', value: 'application/json', type: 'text' }
              ],
              body: {
                mode: 'raw',
                raw: JSON.stringify({ email: 'jan@bedrijf.nl', name: 'Jan de Vries' }, null, 2)
              },
              url: { raw: `${MAILINGLIST_URL}`, protocol: 'https', host: ['app', 'dibbi', 'nl'], path: ['functions', 'v1', 'add-email-contact'] }
            },
            response: []
          }
        ]
      }
    ]
  };

  const downloadPostmanCollection = () => {
    const blob = new Blob([JSON.stringify(postmanCollection, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dibbi-api.postman_collection.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'articles' as TabType, label: 'Articles Webhook', icon: Webhook },
    { id: 'mailinglist' as TabType, label: 'Mailinglijst Webhook', icon: Mail },
  ];

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#231F20] mb-1">API Documentatie</h1>
          <p className="text-gray-500 text-sm">Overzicht van alle beschikbare endpoints en integraties</p>
        </div>
        <button
          onClick={downloadPostmanCollection}
          className="btn-secondary flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Postman Collection
        </button>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'articles' && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <SectionCard title="Endpoint">
              <div className="flex items-center gap-2 mb-3">
                <EndpointBadge method="GET" />
                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-xs text-gray-700 break-all flex items-center justify-between gap-2">
                  <span>{ARTICLES_URL}</span>
                  <button onClick={() => handleCopy(ARTICLES_URL, 'art-url')} className="flex-shrink-0 text-gray-400 hover:text-gray-600">
                    {copiedSection === 'art-url' ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600">Geeft de laatste 5 gepubliceerde artikelen terug.</p>
            </SectionCard>

            <SectionCard title="Authenticatie">
              <p className="text-sm text-gray-600 mb-3">Verplichte request header:</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-sm">
                <span className="text-blue-600">X-API-Key</span>
                <span className="text-gray-400">: </span>
                <span className="text-gray-700">{'<widget_api_key>'}</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">De API key is te vinden in het gebruikersprofiel onder "Widget".</p>
            </SectionCard>

            <SectionCard title="Rate Limiting">
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="font-medium text-gray-800">Limiet:</span> 5 requests per minuut</p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-xs space-y-1">
                  <div>X-RateLimit-Limit: 5</div>
                  <div>X-RateLimit-Remaining: 4</div>
                  <div>X-RateLimit-Reset: ISO timestamp</div>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Response">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                <pre className="text-gray-700">{`{
  "articles": [
    {
      "id": "uuid",
      "slug": "artikel-slug",
      "title": "Artikel Titel",
      "intro": "Intro tekst...",
      "content": "HTML content",
      "featured_image_url": "https://...",
      "published_at": "2026-01-30",
      "view_count": 42
    }
  ],
  "total": 5
}`}</pre>
              </div>
            </SectionCard>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-5">
            <h3 className="font-semibold text-gray-900 mb-5">Code Voorbeelden</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">cURL</p>
                <CodeBlock code={articlesCurl} colorClass="text-green-400" sectionKey="art-curl" copiedSection={copiedSection} onCopy={handleCopy} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">JavaScript</p>
                <CodeBlock code={articlesJs} colorClass="text-yellow-300" sectionKey="art-js" copiedSection={copiedSection} onCopy={handleCopy} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Python</p>
                <CodeBlock code={articlesPython} colorClass="text-blue-300" sectionKey="art-py" copiedSection={copiedSection} onCopy={handleCopy} />
              </div>
            </div>
          </div>

          <SectionCard title="Error Responses">
            <div className="space-y-3">
              {[
                { code: '401', label: 'Unauthorized', body: `{ "error": "API key required in X-API-Key header" }` },
                { code: '403', label: 'Forbidden', body: `{ "error": "Widget disabled" }` },
                { code: '429', label: 'Rate Limit Exceeded', body: `{ "error": "Rate limit exceeded (5 requests per minute)", "reset_at": "..." }` },
                { code: '500', label: 'Internal Server Error', body: `{ "error": "Internal server error" }` },
              ].map(({ code, label, body }) => (
                <div key={code}>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    <span className="text-red-600 font-mono">{code}</span> {label}
                  </p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-xs text-gray-600">{body}</div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {activeTab === 'mailinglist' && (
        <div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-5 flex gap-3">
            <Mail className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-800">
              <p className="font-semibold mb-1">Mailinglijst Webhook</p>
              <p>Voeg e-mailadressen toe aan de mailinglijst van een gebruiker via een extern systeem zoals Zapier of Make.com. Authenticatie via dezelfde <span className="font-mono bg-green-100 px-1 rounded">X-API-Key</span> header als de Articles Webhook. Rate limit: 30 requests/minuut.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <SectionCard title="Endpoint">
              <div className="flex items-center gap-2 mb-3">
                <EndpointBadge method="POST" />
                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-xs text-gray-700 break-all flex items-center justify-between gap-2">
                  <span>{MAILINGLIST_URL}</span>
                  <button onClick={() => handleCopy(MAILINGLIST_URL, 'ml-url')} className="flex-shrink-0 text-gray-400 hover:text-gray-600">
                    {copiedSection === 'ml-url' ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600">Voegt een e-mailadres toe aan de mailinglijst. Als het adres al bestaat wordt het bijgewerkt (naam en subscribed status).</p>
            </SectionCard>

            <SectionCard title="Authenticatie">
              <p className="text-sm text-gray-600 mb-3">Verplichte request header:</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-sm">
                <span className="text-blue-600">X-API-Key</span>
                <span className="text-gray-400">: </span>
                <span className="text-gray-700">{'<widget_api_key>'}</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">Dezelfde API key als de Articles Webhook. Te vinden in het gebruikersprofiel onder "Widget".</p>
            </SectionCard>

            <SectionCard title="Request Body">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-xs overflow-x-auto mb-3">
                <pre className="text-gray-700">{`{
  "email": "jan@bedrijf.nl",  // verplicht
  "name": "Jan de Vries"      // optioneel
}`}</pre>
              </div>
              <p className="text-xs text-gray-500">Het e-mailadres wordt gevalideerd. Ongeldige adressen worden geweigerd met een 400 fout.</p>
            </SectionCard>

            <SectionCard title="Response">
              <p className="text-sm text-gray-600 mb-2">Nieuw contact (<span className="font-mono text-xs">201 Created</span>):</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-xs mb-3">
                <pre className="text-gray-700">{`{ "success": true, "action": "created", "email": "jan@bedrijf.nl" }`}</pre>
              </div>
              <p className="text-sm text-gray-600 mb-2">Bestaand contact bijgewerkt (<span className="font-mono text-xs">200 OK</span>):</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-xs">
                <pre className="text-gray-700">{`{ "success": true, "action": "updated", "email": "jan@bedrijf.nl" }`}</pre>
              </div>
            </SectionCard>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-5">
            <h3 className="font-semibold text-gray-900 mb-5">Code Voorbeelden</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">cURL</p>
                <CodeBlock code={mailinglistCurl} colorClass="text-green-400" sectionKey="ml-curl" copiedSection={copiedSection} onCopy={handleCopy} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">JavaScript</p>
                <CodeBlock code={mailinglistJs} colorClass="text-yellow-300" sectionKey="ml-js" copiedSection={copiedSection} onCopy={handleCopy} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Python</p>
                <CodeBlock code={mailinglistPython} colorClass="text-blue-300" sectionKey="ml-py" copiedSection={copiedSection} onCopy={handleCopy} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Zapier — Code by Zapier (JavaScript)</p>
                <CodeBlock code={mailinglistZapier} colorClass="text-orange-300" sectionKey="ml-zapier" copiedSection={copiedSection} onCopy={handleCopy} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <SectionCard title="Gedrag bij duplicaten">
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex gap-2"><span className="text-green-500 font-bold">+</span> Nieuw adres: wordt aangemaakt met <span className="font-mono text-xs bg-gray-100 px-1 rounded">subscribed: true</span></li>
                <li className="flex gap-2"><span className="text-blue-500 font-bold">~</span> Bestaand adres: naam wordt bijgewerkt als meegegeven</li>
                <li className="flex gap-2"><span className="text-blue-500 font-bold">~</span> Uitgeschreven adres: wordt automatisch weer aangemeld</li>
                <li className="flex gap-2"><span className="text-gray-400 font-bold">-</span> Geen foutmelding bij duplicaat, altijd een succesvolle response</li>
              </ul>
            </SectionCard>

            <SectionCard title="Error Responses">
              <div className="space-y-3">
                {[
                  { code: '400', label: 'Bad Request', body: `{ "error": "email is required" }` },
                  { code: '400', label: 'Bad Request', body: `{ "error": "Invalid email address" }` },
                  { code: '401', label: 'Unauthorized', body: `{ "error": "Invalid API key" }` },
                  { code: '429', label: 'Rate Limit Exceeded', body: `{ "error": "Rate limit exceeded (30 requests per minute)" }` },
                ].map(({ code, label, body }, i) => (
                  <div key={i}>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      <span className="text-red-600 font-mono">{code}</span> {label}
                    </p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-xs text-gray-600">{body}</div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>

          <SectionCard title="Instellen via Zapier">
            <ol className="space-y-3 text-sm text-gray-600">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">1</span>
                Maak een nieuwe Zap aan met de gewenste trigger (bijv. een nieuw formulier, CRM-contact of betaling)
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">2</span>
                Voeg een actie toe: "Code by Zapier" &rarr; "Run JavaScript" en gebruik het bovenstaande Zapier-voorbeeld
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">3</span>
                Vervang <span className="font-mono text-xs bg-gray-100 px-1 rounded">YOUR_API_KEY</span> met de widget API key van de gebruiker (te vinden in AdminUserDetail &rarr; Widget tab)
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">4</span>
                Wijs de e-mail en naam toe via Zapier's "inputData" variabelen vanuit de trigger stap
              </li>
            </ol>
          </SectionCard>
        </div>
      )}
    </AdminLayout>
  );
}
